import React from "react";
import { useContent } from "../context/ContentContext";
import Editable from "./Editable";
import {
  SectionControls,
  ImageEditable,
  resolveColor,
} from "./SectionControls";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Plus, X } from "lucide-react";
import HeroCarousel from "./HeroCarousel";

function Wrap({ section, children, style }) {
  const { editMode, isAdmin } = useContent();
  const editing = editMode && isAdmin;
  return (
    <section className="relative w-full" style={style}>
      {editing && <SectionControls section={section} />}
      {children}
    </section>
  );
}

/* ---------------- HERO ---------------- */
export function HeroSection({ section }) {
  return (
    <Wrap section={section}>
      <HeroCarousel section={section} />
    </Wrap>
  );
}

/* ---------------- BANNER ---------------- */
export function BannerSection({ section }) {
  const { content, updateSection } = useContent();
  const bg = resolveColor(section.bg, content.theme);
  return (
    <Wrap section={section} style={{ backgroundColor: bg }}>
      <div className="mx-auto max-w-5xl px-6 py-10 md:py-14">
        <Editable
          as="h2"
          html={section.text}
          onChange={(t) => updateSection(section.id, { text: t })}
          className="font-heading text-center text-2xl font-bold leading-snug md:text-4xl"
          style={{ color: section.color }}
        />
      </div>
    </Wrap>
  );
}

/* ---------------- RICH TEXT ---------------- */
export function RichTextSection({ section }) {
  const { content, updateSection, editMode, isAdmin } = useContent();
  const editing = editMode && isAdmin;
  const bg = resolveColor(section.bg, content.theme);

  const setPara = (i, val) => {
    const paragraphs = [...section.paragraphs];
    paragraphs[i] = val;
    updateSection(section.id, { paragraphs });
  };
  const addPara = () =>
    updateSection(section.id, {
      paragraphs: [...section.paragraphs, "New paragraph. Click to edit."],
    });
  const removePara = (i) =>
    updateSection(section.id, {
      paragraphs: section.paragraphs.filter((_, idx) => idx !== i),
    });

  return (
    <Wrap section={section} style={{ backgroundColor: bg }}>
      <div className="mx-auto max-w-3xl px-6 py-10 md:py-14">
        {section.paragraphs.map((p, i) => (
          <div key={i} className="relative mb-5 last:mb-0">
            {editing && section.paragraphs.length > 1 && (
              <button
                onClick={() => removePara(i)}
                title="Remove paragraph"
                className="absolute -left-8 top-1 rounded p-1 text-red-500 hover:bg-red-50"
              >
                <X size={16} />
              </button>
            )}
            <Editable
              as="p"
              html={p}
              onChange={(v) => setPara(i, v)}
              className="text-center text-[17px] leading-relaxed"
              style={{ color: section.color }}
            />
          </div>
        ))}
        {editing && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={addPara}
              className="flex items-center gap-1 rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
            >
              <Plus size={15} /> Add paragraph
            </button>
          </div>
        )}
      </div>
    </Wrap>
  );
}

/* ---------------- BULLETS ---------------- */
export function BulletsSection({ section }) {
  const { content, updateSection, editMode, isAdmin } = useContent();
  const editing = editMode && isAdmin;
  const bg = resolveColor(section.bg, content.theme);

  const setGroups = (groups) => updateSection(section.id, { groups });

  const setHeading = (gi, val) => {
    const groups = section.groups.map((g, i) =>
      i === gi ? { ...g, heading: val } : g
    );
    setGroups(groups);
  };
  const setItem = (gi, ii, val) => {
    const groups = section.groups.map((g, i) =>
      i === gi
        ? { ...g, items: g.items.map((it, j) => (j === ii ? val : it)) }
        : g
    );
    setGroups(groups);
  };
  const addItem = (gi) => {
    const groups = section.groups.map((g, i) =>
      i === gi ? { ...g, items: [...g.items, "New bullet point"] } : g
    );
    setGroups(groups);
  };
  const removeItem = (gi, ii) => {
    const groups = section.groups.map((g, i) =>
      i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g
    );
    setGroups(groups);
  };
  const addGroup = () =>
    setGroups([
      ...section.groups,
      { heading: "New heading:", items: ["New bullet point"] },
    ]);
  const removeGroup = (gi) =>
    setGroups(section.groups.filter((_, i) => i !== gi));

  return (
    <Wrap section={section} style={{ backgroundColor: bg }}>
      <div className="mx-auto max-w-3xl px-6 py-10 md:py-14">
        {section.groups.map((g, gi) => (
          <div key={gi} className="relative mb-8 last:mb-0">
            {editing && section.groups.length > 1 && (
              <button
                onClick={() => removeGroup(gi)}
                title="Remove group"
                className="absolute -left-8 top-1 rounded p-1 text-red-500 hover:bg-red-50"
              >
                <X size={16} />
              </button>
            )}
            <Editable
              as="h3"
              html={g.heading}
              onChange={(v) => setHeading(gi, v)}
              singleLine
              className="font-heading mb-3 text-2xl font-bold"
              style={{ color: section.color }}
            />
            <ul className="space-y-2.5">
              {g.items.map((it, ii) => (
                <li key={ii} className="relative flex items-start gap-3">
                  <span
                    className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: section.color }}
                  />
                  <Editable
                    as="div"
                    html={it}
                    onChange={(v) => setItem(gi, ii, v)}
                    className="flex-1 text-[17px] leading-relaxed"
                    style={{ color: section.color }}
                  />
                  {editing && (
                    <button
                      onClick={() => removeItem(gi, ii)}
                      title="Remove"
                      className="rounded p-1 text-red-500 hover:bg-red-50"
                    >
                      <X size={15} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {editing && (
              <button
                onClick={() => addItem(gi)}
                className="mt-3 flex items-center gap-1 rounded border border-slate-400/40 px-3 py-1.5 text-sm hover:bg-black/5"
                style={{ color: section.color }}
              >
                <Plus size={15} /> Add bullet
              </button>
            )}
          </div>
        ))}
        {editing && (
          <button
            onClick={addGroup}
            className="mt-4 flex items-center gap-1 rounded border border-slate-400/40 px-3 py-1.5 text-sm hover:bg-black/5"
            style={{ color: section.color }}
          >
            <Plus size={15} /> Add list group
          </button>
        )}
      </div>
    </Wrap>
  );
}

/* ---------------- ELECTION INFO ---------------- */
export function ElectionSection({ section }) {
  const { content, updateSection, editMode, isAdmin } = useContent();
  const editing = editMode && isAdmin;
  const bg = resolveColor(section.bg, content.theme);

  const setItem = (i, patch) =>
    updateSection(section.id, {
      items: section.items.map((it, j) =>
        j === i ? { ...it, ...patch } : it
      ),
    });
  const addItem = () =>
    updateSection(section.id, {
      items: [...section.items, { label: "Label:", value: "Value" }],
    });
  const removeItem = (i) =>
    updateSection(section.id, {
      items: section.items.filter((_, j) => j !== i),
    });

  return (
    <Wrap section={section} style={{ backgroundColor: bg }}>
      <div className="mx-auto max-w-3xl px-6 py-10 text-center md:py-14">
        <Editable
          as="h2"
          html={section.heading}
          onChange={(v) => updateSection(section.id, { heading: v })}
          singleLine
          className="font-heading mb-8 text-3xl font-bold"
          style={{ color: section.color }}
        />
        <div className="mx-auto grid max-w-xl gap-6 sm:grid-cols-2">
          {section.items.map((it, i) => (
            <div key={i} className="relative">
              {editing && (
                <button
                  onClick={() => removeItem(i)}
                  className="absolute right-0 top-0 rounded p-1 text-red-500 hover:bg-red-50"
                >
                  <X size={15} />
                </button>
              )}
              <Editable
                as="div"
                html={it.label}
                onChange={(v) => setItem(i, { label: v })}
                singleLine
                className="font-heading text-lg font-semibold"
                style={{ color: section.color }}
              />
              <Editable
                as="div"
                html={it.value}
                onChange={(v) => setItem(i, { value: v })}
                className="mt-1 text-[16px] leading-relaxed"
                style={{ color: section.color }}
              />
            </div>
          ))}
        </div>
        {editing && (
          <div className="mt-6">
            <button
              onClick={addItem}
              className="inline-flex items-center gap-1 rounded border border-slate-400/40 px-3 py-1.5 text-sm hover:bg-black/5"
              style={{ color: section.color }}
            >
              <Plus size={15} /> Add item
            </button>
          </div>
        )}
        {section.footnote != null && (
          <Editable
            as="h3"
            html={section.footnote}
            onChange={(v) => updateSection(section.id, { footnote: v })}
            className="font-heading mx-auto mt-10 max-w-2xl text-2xl font-bold leading-snug"
            style={{ color: section.color }}
          />
        )}
      </div>
    </Wrap>
  );
}

/* ---------------- FAQ ---------------- */
export function FaqSection({ section }) {
  const { content, updateSection, editMode, isAdmin } = useContent();
  const editing = editMode && isAdmin;
  const bg = resolveColor(section.bg, content.theme);

  const setItem = (i, patch) =>
    updateSection(section.id, {
      items: section.items.map((it, j) =>
        j === i ? { ...it, ...patch } : it
      ),
    });
  const addItem = () =>
    updateSection(section.id, {
      items: [
        ...section.items,
        { q: "Your question?", a: "Your answer goes here." },
      ],
    });
  const removeItem = (i) =>
    updateSection(section.id, {
      items: section.items.filter((_, j) => j !== i),
    });

  return (
    <Wrap section={section} style={{ backgroundColor: bg }}>
      <div className="mx-auto max-w-3xl px-6 py-12 md:py-16">
        <Editable
          as="h2"
          html={section.heading}
          onChange={(v) => updateSection(section.id, { heading: v })}
          singleLine
          className="font-heading mb-8 text-center text-3xl font-bold"
          style={{ color: section.color }}
        />
        {editing ? (
          <div className="space-y-4">
            {section.items.map((it, i) => (
              <div
                key={i}
                className="relative rounded-lg border border-slate-300 bg-white/60 p-4"
              >
                <button
                  onClick={() => removeItem(i)}
                  className="absolute right-2 top-2 rounded p-1 text-red-500 hover:bg-red-50"
                >
                  <X size={16} />
                </button>
                <Editable
                  as="div"
                  html={it.q}
                  onChange={(v) => setItem(i, { q: v })}
                  className="font-heading pr-8 text-lg font-semibold"
                  style={{ color: section.color }}
                />
                <Editable
                  as="div"
                  html={it.a}
                  onChange={(v) => setItem(i, { a: v })}
                  className="mt-2 text-[16px] leading-relaxed text-slate-700"
                />
              </div>
            ))}
            <button
              onClick={addItem}
              className="flex items-center gap-1 rounded border border-slate-400/50 px-3 py-1.5 text-sm hover:bg-black/5"
              style={{ color: section.color }}
            >
              <Plus size={15} /> Add question
            </button>
          </div>
        ) : (
          <Accordion type="single" collapsible className="w-full">
            {section.items.map((it, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-slate-400/40"
              >
                <AccordionTrigger
                  className="font-heading py-4 text-left text-lg font-semibold hover:no-underline"
                  style={{ color: section.color }}
                >
                  <span dangerouslySetInnerHTML={{ __html: it.q }} />
                </AccordionTrigger>
                <AccordionContent className="text-[16px] leading-relaxed text-slate-700">
                  <span dangerouslySetInnerHTML={{ __html: it.a }} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </Wrap>
  );
}

/* ---------------- FOOTER LOGO ---------------- */
export function FooterLogoSection({ section }) {
  const { content, updateSection } = useContent();
  const bg = resolveColor(section.bg, content.theme);
  return (
    <Wrap section={section} style={{ backgroundColor: bg }}>
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-6 py-12">
        <CampaignLogo />
        <Editable
          as="p"
          html={section.tagline}
          onChange={(v) => updateSection(section.id, { tagline: v })}
          singleLine
          className="text-sm text-slate-500"
        />
      </div>
    </Wrap>
  );
}

/* Campaign logo — editable image stored in header.logoUrl. */
export function CampaignLogo({ compact = false }) {
  const { content, updateHeader } = useContent();
  return (
    <ImageEditable
      src={content.header.logoUrl || "/logo-yes-on-o.png"}
      alt="Invest in Local Kids — Yes on O"
      onChange={(url) => updateHeader({ logoUrl: url })}
      className={`${compact ? "w-56" : "w-72 md:w-96"} h-auto`}
      style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.25))" }}
    />
  );
}

export const SECTION_COMPONENTS = {
  hero: HeroSection,
  banner: BannerSection,
  richtext: RichTextSection,
  bullets: BulletsSection,
  election: ElectionSection,
  faq: FaqSection,
  footerlogo: FooterLogoSection,
};
